import {DatePicker, Form, InputNumber, Tag} from 'antd';
import moment from 'moment';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {QUILL_FORMATS, QUILL_MODULES} from 'utils/constants';
import {isAnalysisEmpty, sanitizeAnalysis} from '../constants';
import {InstrumentSelect} from './InstrumentSelect';
import styles from './workspace.module.scss';

/**
 * Form-friendly ReactQuill wrapper.
 *
 * react-quill 1.3.5 preserves the type of whatever value it is first given and
 * echoes a tainted Delta back through `onChange`; if that Delta ever returns as
 * `value` the editor throws ("passing the `delta` object … back as `value`").
 * Coercing `value` to a string and forwarding only the HTML content keeps the
 * field a plain string on both legs, so the Delta can never round-trip.
 */
const AnalysisEditor = ({value, onChange, ...rest}) => (
  <ReactQuill
    theme="snow"
    value={typeof value === 'string' ? value : ''}
    onChange={content => onChange?.(content)}
    {...rest}
  />
);

/**
 * Builds the pick payload every write shares.
 *
 * `ticker`, issuer, `order`, `source` and `type` are deliberately absent:
 * ticker is derived from `instrumentId` and rejected when sent, issuer is
 * display metadata, order is assigned by the backend, and source/type come from
 * the enclosing mutation.
 */
export const buildPickInput = (values, exchangeId) => ({
  instrumentId: values.instrumentId,
  exchange: exchangeId,
  priceAsOf: Number(values.priceAsOf),
  priceAsOfDate: moment(values.priceAsOfDate).toISOString(),
  projectedPriceBefore: Number(values.projectedPriceBefore),
  projectedPriceBeforeDate: values.projectedPriceBeforeDate
    ? moment(values.projectedPriceBeforeDate).toISOString()
    : null,
  analysis: sanitizeAnalysis(values.analysis),
});

const positivePrice = label => ({
  validator: async (rule, value) => {
    if (!(value > 0)) throw new Error(`${label} must be greater than zero`);
  },
});

const analysisRule = {
  validator: async (rule, value) => {
    if (isAnalysisEmpty(value)) throw new Error('Write an analysis');
  },
};

/**
 * The pick form body — shared by add-one, edit-one and each bulk manual row.
 *
 * Exchange is shown as a read-only badge: the backend rejects a pick whose
 * exchange differs from its parent, so there is no second selector here.
 */
export const PickFields = ({
  form,
  namePath = [],
  fieldPath,
  exchange,
  seed,
  instrumentDisabled,
}) => {
  // `namePath` is relative to an enclosing Form.List, but `form.setFields`
  // addresses the whole form — inside a list the two differ.
  const path = field => [...namePath, field];
  const absolute = field => [...(fieldPath || namePath), field];
  const currencyCode = exchange?.baseCurrencyCode;

  const handleInstrumentSelected = instrument => {
    if (!instrument) return;
    // Selecting an instrument is an explicit action, so its latest quote seeds
    // the "price as of" pair the operator would otherwise retype.
    form.setFields([
      {name: absolute('priceAsOf'), value: instrument.lastPrice ?? undefined},
      {
        name: absolute('priceAsOfDate'),
        value: instrument.priceAsOf ? moment(instrument.priceAsOf) : moment(),
      },
    ]);
  };

  return (
    <div className={styles.pickGrid}>
      <div className={styles.exchangeBadgeRow}>
        <span className={styles.fieldLabel}>Exchange</span>
        <Tag className={styles.softTag}>
          {exchange?.code}
          {currencyCode ? ` · ${currencyCode}` : ''}
        </Tag>
        <span className={styles.mutedText}>
          Set on the recommendation — every pick uses it.
        </span>
      </div>

      <Form.Item
        className={styles.spanFull}
        label="Instrument"
        name={path('instrumentId')}
        rules={[{required: true, message: 'Select an instrument'}]}>
        <InstrumentSelect
          exchangeCode={exchange?.code}
          seed={seed}
          disabled={instrumentDisabled}
          onSelectInstrument={handleInstrumentSelected}
        />
      </Form.Item>

      <Form.Item
        label="Price as of"
        name={path('priceAsOf')}
        rules={[
          {required: true, message: 'Enter the current price'},
          positivePrice('Price'),
        ]}>
        <InputNumber
          className={styles.fullWidth}
          addonAfter={currencyCode}
          placeholder="0.00"
        />
      </Form.Item>

      <Form.Item
        label="Price date"
        name={path('priceAsOfDate')}
        rules={[{required: true, message: 'Select the price date'}]}>
        <DatePicker className={styles.fullWidth} />
      </Form.Item>

      <Form.Item
        label="Target price"
        name={path('projectedPriceBefore')}
        rules={[
          {required: true, message: 'Enter the target price'},
          positivePrice('Target price'),
        ]}>
        <InputNumber
          className={styles.fullWidth}
          addonAfter={currencyCode}
          placeholder="0.00"
        />
      </Form.Item>

      <Form.Item
        label="Target date"
        name={path('projectedPriceBeforeDate')}
        // Schema-optional, but the PDF renders an incomplete date without it.
        rules={[{required: true, message: 'Select the target date'}]}>
        <DatePicker className={styles.fullWidth} />
      </Form.Item>

      <Form.Item
        className={styles.spanFull}
        label="Analysis"
        name={path('analysis')}
        rules={[{required: true, message: 'Write an analysis'}, analysisRule]}>
        <AnalysisEditor
          className={styles.quill}
          modules={QUILL_MODULES}
          formats={QUILL_FORMATS}
          placeholder="Fundamental and valuation analysis…"
        />
      </Form.Item>
    </div>
  );
};

export default PickFields;
